// @NA 2025-09-22: Separate Evidence and Retail gallery pages

import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "../../contexts/TranslationContext";
import { useGlobalPopup } from "../../contexts/GlobalPopupContext";
import api from '../../utils/api';

import PageTitle from "../UC/PageTitle";
import Page from "../UC/Page";

import EvidenceFilter from "./EvidenceFilter";
import GalleryCard from "./GalleryCard";
import GalleryList from "./GalleryList";
import Details from "../Modals/Details";
import Import from "./Import";
import Pagination from "../UC/Pagination";
import ViewStyle from "../UC/ViewStyle";
import Loader from "../UC/Loader";
import NoRecordFound from "../UC/NoRecordFound";

import "../../assets/css/evidence.scss";

const Evidence = ({ selectionMode, onClose, onSelect }) => {
    const navigate = useNavigate();
    const dialogRef = useRef(null);
    const editDialogRef = useRef(null);
    const abortControllerRef = useRef(null);
    const { showDeleteConfirm, showError, showSuccess, showWarning, showConfirm } = useGlobalPopup();
    const { language } = useTranslation();

    const svgIconEvidence = (
        <svg

            aria-hidden="true"
            xmlns="http://www.w3.org/2000/svg"
            width="20"
            height="24"
            fill="none"
            viewBox="0 0 20 24"
        >
            {/* Outer rectangle frame */}
            <rect
                x="1"
                y="1"
                width="18"
                height="22"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeDasharray="0"
                fill="none"
                rx="2"
            />

            {/* Upper sole shape (toe area) */}
            <path
                stroke="currentColor"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="1"
                fill="rgb(59, 130, 246)"
                d="M10.2 3.5035c-2.5025 0-3.5035 2.5025-3.5035 4.5045 0 .8008.2002 1.5015.4004 2.2022.1001.4004.2002.8008.2002 1.1011.1001.3003.2002.6006.4004.8008.3003.3003.8008.4004 1.3013.4004h2.4024c.5005 0 1.001-.1001 1.3013-.4004.2002-.2002.3003-.5005.4004-.8008 0-.3003.1001-.7007.2002-1.1011.2002-.7007.4004-1.4014.4004-2.2022 0-2.002-1.001-4.5045-3.5035-4.5045z"
            />

            {/* Lower sole shape (heel area) */}
            <path
                stroke="currentColor"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="1"
                fill="rgb(59, 130, 246)"
                d="M7.724 15.224c0 .5616 0 1.1232.156 1.6848.156.7488.312 1.4976.624 2.0592.468.7488 1.248.7488 2.028.7488s1.56 0 2.028-.7488c.312-.5616.468-1.3104.624-2.0592.156-.5616.156-1.1232.156-1.6848H7.724z"
            />
        </svg>
    );

    // Check if current user has permission to edit/delete a record
    const checkUserPermission = (record) => {
        try {
            const userData = localStorage.getItem("FWUser");
            if (!userData) {
                return false;
            }

            const parsedUser = JSON.parse(userData);

            return parsedUser.Information?.User_ID === record.User_IDValue && parsedUser.Information?.User_ID_Ext === record.User_ID_ExtValue; // Ensure both are strings for comparison
        } catch (error) {
            console.error("Error checking user permission:", error);
            return false;
        }
    };

    const loginAccount = useMemo(() => {
        const stored = localStorage.getItem("FWUser");
        return stored ? JSON.parse(stored) : null;
    }, []);

    const defaultFilterEvidence = useMemo(() => ({
        Scanner_IDs: [loginAccount && loginAccount.Information ? `${loginAccount.Information.User_ID}-${loginAccount.Information.User_ID_Ext}` : null].filter(id => id),
        Category_IDs: ["2", "3"], // Default to both types for evidence
        CaseIncidents: [],
    }), [loginAccount]);

    const [selectedRecord, setSelectedRecord] = useState();
    const [editRecord, setEditRecord] = useState(null);
    const [selectedId, setSelectedId] = useState('0');
    const [viewMode, setViewMode] = useState('grid');
    const [filters, setFilters] = useState(null);
    const [galleries, setGalleries] = useState([]);
    const [resultSummary, setResultSummary] = useState(0);
    const [arrRelatedAccounts, setArrRelatedAccounts] = useState([]);
    const [pageIndex, setPageIndex] = useState(1);
    const [pageSize, setPageSize] = useState(50);
    const [isSearching, setIsSearching] = useState(false);
    const [isFilterReady, setIsFilterReady] = useState(false);
    const [isResettingFilters, setIsResettingFilters] = useState(false);

    const handleEditGallery = (record) => {

        if (!checkUserPermission(record)) {
            showWarning(
                language.gallery?.noPermission || "No Permission",
                language.gallery?.noEditPermission || "You do not have permission to edit this item. You can only edit items you created."
            );
            return;
        }

        console.log("Editing gallery:", record);
        setEditRecord(record);
        if (editDialogRef.current && editDialogRef.current.showModal) {
            editDialogRef.current.showModal();
        }
    };

    const handleCloseEditModal = () => {
        setEditRecord(null);
        if (editDialogRef.current && editDialogRef.current.close) {
            editDialogRef.current.close();
        }
    };

    const handleEditSuccess = () => {
        // Only refresh gallery data when edit is actually successful
        console.log("Edit successful, refreshing gallery data");
        searchGallery(pageIndex, pageSize, filters);
    };

    const handleDeleteGallery = (record) => {
        // Check if current user has permission to delete this record
        if (!checkUserPermission(record)) {
            showWarning(
                language.gallery?.noPermission || 'No Permission',
                language.gallery?.noDeletePermission || 'You do not have permission to delete this item. You can only delete items you created.'
            );
            return;
        }

        let itemName = `${record.ImpressionInfo?.CaseIncident} #${record.ImpressionInfo?.ImpressionNumber}` || language.gallery?.thisItem || 'this item';

        if (!record.ImpressionInfo?.CaseIncident && !record.ImpressionInfo?.ImpressionNumber)
            itemName = `this item`;

        showDeleteConfirm(
            language.gallery?.deleteGalleryItem || 'Delete Gallery Item',
            `${language.gallery?.deleteConfirmMessage || 'Are you sure you want to delete'} "${itemName}"? ${language.gallery?.deleteActionWarning || 'This action cannot be undone.'}`,
            async () => {
                try {
                    console.log("Deleting gallery:", record);

                    // Use api.post with evidence endpoint and send ID as array
                    const response = await api.post(`${global.config.api.domain}/fwiq-api/api/evidence/delete`,
                        [record.IDValue] // Send ID as an array
                    );

                    if (response.status === 200 || response.status === 204) {
                        showSuccess(language.gallery?.success || 'Success', `"${itemName}" ${language.gallery?.deleteSuccessMessage || 'has been deleted successfully.'}`);
                        // Refresh the gallery data after delete
                        searchGallery(pageIndex, pageSize, filters);
                    } else {
                        showError(language.gallery?.deleteFailed || 'Delete Failed', language.gallery?.deleteFailedMessage || 'Failed to delete the item. Please try again.');
                    }
                } catch (error) {
                    console.error("Error deleting gallery item:", error);
                    showError(language.gallery?.deleteFailed || 'Delete Failed', language.gallery?.deleteErrorMessage || 'An error occurred while deleting the item. Please try again.');
                }
            }
        );
    };

    const handleReUpload = (record) => {
        let itemName = `${record.ImpressionInfo?.CaseIncident} #${record.ImpressionInfo?.ImpressionNumber}` || language.gallery?.thisItem || 'this item';

        if (!record.ImpressionInfo?.CaseIncident && !record.ImpressionInfo?.ImpressionNumber)
            itemName = `this item`;

        showConfirm(
            language.gallery?.reUploadConfirmTitle || 'Re-Upload Confirmation',
            `${language.gallery?.reUploadConfirmMessage || 'Are you sure you want to re-upload this item? This will process the images again.'} "${itemName}"`,
            async () => {
                try {
                    console.log("Re-uploading evidence item:", record);

                    // Use api.post with evidence reupload endpoint
                    const response = await api.post(`${global.config.api.domain}/fwiq-api/api/evidence/reupload`,
                        [record.IDValue] // Send IDValue as an array
                    );

                    if (response.status === 200 || response.status === 204) {
                        showSuccess(language.gallery?.reUploadSuccess || 'Success', `"${itemName}" ${language.gallery?.reUploadSuccessMessage || 'has been re-uploaded successfully.'}`);
                        // Refresh the gallery data after re-upload
                        searchGallery(pageIndex, pageSize, filters);
                    } else {
                        showError(language.gallery?.reUploadError || 'Re-Upload Failed', language.gallery?.reUploadErrorMessage || 'Failed to re-upload the item. Please try again.');
                    }
                } catch (error) {
                    console.error("Error re-uploading evidence item:", error);
                    showError(language.gallery?.reUploadError || 'Re-Upload Failed', language.gallery?.reUploadErrorMessage || 'Failed to re-upload the item. Please try again.');
                }
            }
        );
    };

    const searchGallery = useCallback((appliedPageIndex = pageIndex, appliedPageSize = pageSize, appliedFilters = filters) => {
        if (!appliedFilters) return;

        // Cancel previous request if it exists
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
        }

        // Create new AbortController for this request
        abortControllerRef.current = new AbortController();

        setIsSearching(true);

        const _tokenRequestAPI = localStorage.getItem("FWToken");

        const searchPayload = {
            PageIndex: appliedPageIndex,
            PageSize: appliedPageSize,
            ...appliedFilters
        };

        api.post(`${global.config.api.domain}/fwiq-api/api/evidence/search`, searchPayload, {
            signal: abortControllerRef.current.signal
        })
            .then(response => {
                const data = response.data;
                if (data && data.Records) {
                    setGalleries(data.Records || []);
                    setResultSummary(data.TotalRecords || 0);
                    setArrRelatedAccounts(data.RelatedAccounts || []);
                } else {
                    setGalleries([]);
                    setResultSummary(0);
                    setArrRelatedAccounts([]);
                }
            })
            .catch(error => {
                if (error.name === 'AbortError' || error.code === 'ERR_CANCELED') return;
                if (error.response?.status === 401) return; // Let axiosConfig interceptor handle logout
                console.error('Error searching gallery:', error);
                setGalleries([]);
                setResultSummary(0);
                setArrRelatedAccounts([]);
            })
            .finally(() => {
                if (abortControllerRef.current && !abortControllerRef.current.signal.aborted) {
                    setIsSearching(false);
                }
            });
    }, [filters, pageIndex, pageSize]);

    // Reset to index 1 when filters change
    useEffect(() => {
        if (filters) {
            setPageIndex(1);
        }
    }, [filters]);

    useEffect(() => {
        if (selectedRecord) {
            const newSelectedId = selectedRecord?.IDValue || '0';
            setSelectedId(newSelectedId);

            // Open details modal if not in selection mode
            if (!selectionMode && dialogRef.current && dialogRef.current.showModal) {
                dialogRef.current.showModal();
            }
        } else {
            setSelectedId('0');
        }
    }, [selectedRecord, selectionMode]);

    // Initialize filters when filter is ready
    useEffect(() => {
        if (!isFilterReady) {
            setIsSearching(true);
            return;
        }

        if (!filters) {

            setFilters(defaultFilterEvidence);
        }

    }, [isFilterReady, filters, defaultFilterEvidence]);

    // Separate useEffect to handle searchGallery when filters are ready.
    // Debounce (300ms) prevents double-fire when filters change AND pageIndex
    // resets to 1 in the same tick — only the final state triggers a fetch.
    useEffect(() => {
        if (!isFilterReady || !filters) {
            if (!isFilterReady) setIsSearching(true);
            return;
        }

        const debounceTimer = setTimeout(() => {
            searchGallery(pageIndex, pageSize, filters);
        }, 300);

        return () => clearTimeout(debounceTimer);
    }, [filters, pageIndex, pageSize, searchGallery, isFilterReady]);

    // Cleanup function to abort requests on component unmount
    useEffect(() => {
        return () => {
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
            }
        };
    }, []);

    const handleApplyFilters = (newFilters) => {

        if (!newFilters) return;

        if (!newFilters.CaseIncidents === undefined && newFilters.Scanner_IDs === undefined)
        {
            setFilters(defaultFilterEvidence);
            setPageIndex(1);
        }
        else
        {
            setFilters(newFilters);
            setPageIndex(1);
        }
    };

    // Callback for when EvidenceFilter is ready
    const handleFilterReady = useCallback((isReady) => {
        setIsFilterReady(isReady);
    }, []);

    const handleFocusRecord = (record) => {
        // Set the selected ID to highlight the card
        setSelectedId(record.IDValue);

        if (selectionMode) {
            onSelect(record);
        }
    };

    const handleOpenDetails = (record) => {
        if (selectionMode && onSelect) {
            // In selection mode, double-click selects the record
            setSelectedId(record.IDValue);
            onSelect(record);
        } else {
            // In normal mode, double-click opens details
            setSelectedRecord(record);
        }
    };

    // Handle analyze functionality - navigate to analyze page with selected record
    const handleAnalyze = (record) => {
        // Store the selected record in sessionStorage so it can be retrieved by the Analyze component
        sessionStorage.setItem('selectedAnalyzeRecord', JSON.stringify(record));
        // Navigate to analyze page
        navigate('/analyze');
    };

    // Page header content
    const pageHeaderContent = (
        <div className="header-content">
            <PageTitle
                title={language.evidence?.pageTitle || "Impression Evidence"}
                subtitle={language.evidence?.pageSubtitle || "View and manage your impression evidence records"}
                icon={svgIconEvidence}
            />
            <div className="header-actions">
                <ViewStyle viewMode={viewMode} setViewMode={setViewMode} />
            </div>
        </div>
    );

    // Sidebar filter content
    const sidebarFilterContent = (
        <>
            <div className="sidebar-header">
                <div className="filters-header">
                    <i className="fa fa-filter"></i>
                    <span>{language.buttons?.filters || "Filters"}</span>
                </div>
                <div className="filters-actions">
                    <button className="btn btn-default" onClick={() => {
                        setFilters(defaultFilterEvidence);
                        setIsResettingFilters(true); // Toggle to trigger reset in child

                        setTimeout(() => {
                            setIsResettingFilters(false); // Reset the toggle after a short delay
                        }, 100);
                    }}>
                        <i className="fa fa-refresh"></i>
                        {language.buttons?.resetAll || "Reset All"}
                    </button>
                </div>
            </div>

            <EvidenceFilter
                defaultFilters={defaultFilterEvidence}
                applyFilters={handleApplyFilters}
                onFilterReady={handleFilterReady}
                className="page-filters"
                resetFiltersTrigger={isResettingFilters} // Can be used to trigger reset from parent if needed
            />
        </>
    );

    // Search result content
    const searchResultContent = (
        <>
            <div className={`result-view ${viewMode}-view ${isSearching ? 'loading' : ''}`}>
                {isSearching ? (
                    <Loader text={language.messages?.loadingEvidence || "Loading evidence records..."} />
                ) : galleries.length === 0 ? (
                    <NoRecordFound language={language} />
                ) : viewMode === 'list' ? (
                    <GalleryList
                        galleries={galleries}
                        pageIndex={pageIndex}
                        pageSize={pageSize}
                        selectedId={selectedId}
                        galleryType="evidence"
                        selectionMode={selectionMode}
                        handleFocusRecord={handleFocusRecord}
                        handleOpenDetails={handleOpenDetails}
                        handleEditGallery={handleEditGallery}
                        handleDeleteGallery={handleDeleteGallery}
                        handleReUpload={handleReUpload}
                    />
                ) : (
                    <div className={`results ${viewMode}`}>
                        {galleries.map((record, idx) => (
                            <GalleryCard
                                key={record.IDValue}
                                index={(pageIndex - 1) * pageSize + idx + 1}
                                record={record}
                                isSelected={record.IDValue === selectedId}
                                selectRecord={() => handleFocusRecord(record)}
                                viewDetails={() => handleOpenDetails(record)}
                                hideActions={selectionMode}
                                analyzeFunc={handleAnalyze}
                                editFunc={(e) => {
                                    e.stopPropagation();
                                    handleEditGallery(record);
                                }}
                                deleteFunc={(e) => {
                                    e.stopPropagation();
                                    handleDeleteGallery(record);
                                }}
                            />
                        ))}
                    </div>
                )}
            </div>

            <div className="result-summary">
                <Pagination
                    totalRecords={resultSummary || 0}
                    pageIndex={pageIndex}
                    pageSize={pageSize}
                    changePageIndex={(newPageIndex) => {
                        setPageIndex(newPageIndex);
                    }}
                    changePageSize={(newPageSize) => {
                        setPageSize(newPageSize);
                        setPageIndex(1); // Reset to first page when page size changes
                    }}
                />
            </div>
        </>
    );

    return (
        <>
            <Page
                extentClassName="evidence"
                pageHeaderContent={pageHeaderContent}
                sidebarFilterContent={sidebarFilterContent}
                searchResultContent={searchResultContent}
            />

            {/* Details Dialog */}
            <dialog ref={dialogRef} className="dialog">
                {selectedRecord && (
                    <Details
                        arrAccounts={arrRelatedAccounts}
                        record={selectedRecord}
                        onClose={() => {
                            if (dialogRef.current) {
                                dialogRef.current.close();
                            }
                            setSelectedRecord(null);
                        }}
                    />
                )}
            </dialog>

            {/* Edit Dialog */}
            <dialog ref={editDialogRef} className="dialog edit-dialog">
                {editRecord && (
                    <Import
                        isModal={true}
                        editId={editRecord.IDValue}
                        onClose={handleCloseEditModal}
                        onEditSuccess={handleEditSuccess}
                        arrAccounts={arrRelatedAccounts}
                    />
                )}
            </dialog>

            {/* Selection Mode Close Button */}
            {selectionMode && onClose && (
                <button className="selection-close-btn" onClick={onClose}>
                    <i className="fa fa-times"></i>
                    {language.buttons?.close || "Close"}
                </button>
            )}
        </>
    );
};

export default Evidence;